import React, { useState } from "react";
import { Pencil, Check, X, Quote } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * TaglineEditor — short 1-line catchphrase that renders under the bio.
 * 100 chars max. Inline edit (click pencil), keyboard shortcuts: Enter saves,
 * Esc cancels. Empty string = clear.
 */
export default function TaglineEditor({ value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy) return;
    const trimmed = text.trim().slice(0, 100);
    setBusy(true);
    try {
      await api.put("/me/profile", { tagline: trimmed });
      toast.success(trimmed ? "Tagline saved" : "Tagline cleared");
      onSaved?.(trimmed || null);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2 mt-2 border border-white/10 bg-[#0a0a0a] px-4 py-2" data-testid="profile-tagline-display">
        <Quote size={11} className="text-zinc-500 shrink-0" />
        <div className="flex-1 min-w-0">
          {value ? (
            <p className="text-sm italic text-zinc-300 truncate">"{value}"</p>
          ) : (
            <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-600">
              // ADD A TAGLINE TO INTRODUCE YOURSELF
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => { setText(value || ""); setEditing(true); }}
          data-testid="profile-tagline-edit-btn"
          className="text-zinc-500 hover:text-white p-1 transition-colors shrink-0"
          aria-label="Edit tagline"
        >
          <Pencil size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid="profile-tagline-editor">
      <input
        autoFocus
        type="text"
        maxLength={100}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        placeholder="One line that introduces you…"
        data-testid="profile-tagline-input"
        className="w-full bg-[#050505] border border-white/20 focus:border-[#ffd60a] outline-none px-3 py-2 text-sm text-white italic"
      />
      <div className="flex items-center justify-between mt-1">
        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
          {text.length}/100
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(false)}
            data-testid="profile-tagline-cancel"
            className="text-zinc-400 hover:text-white p-1 transition-colors"
            aria-label="Cancel"
          >
            <X size={12} />
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="profile-tagline-save"
            className="text-[#ffd60a] hover:text-white p-1 transition-colors disabled:opacity-50"
            aria-label="Save tagline"
          >
            <Check size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
